import type { DictKey } from './en'

/** Chinese dictionary — must cover every key of `en` (enforced by types). */
export const zh: Record<DictKey, string> = {
  // Navigation
  navTools: '工具',
  navApi: 'API',
  navPricing: '定价',
  navStart: '开始转换',
  navMenu: '切换导航菜单',

  // Common
  commonTryAgain: '重试',
  commonErrorDefault: '出错了，请重试。',
  commonDownload: '下载',
  commonNewFile: '新文件',
  commonBackHome: '返回首页',
  commonRemoveFile: '移除 {name}',

  // Hero
  heroBadge: '100% 浏览器内完成 — 文件永不离开你的设备',
  heroTitle1: '你的文件，',
  heroTitle2: '你的浏览器，',
  heroTitle3: '你的隐私。',
  heroSubtitle: '无需上传即可转换、压缩和管理文件。',
  heroCtaStart: '开始转换',
  heroCtaExplore: '探索工具',
  heroNote: '无需注册 · 无需上传 · 不收集数据',

  // Home tools grid
  homeToolsTitle: '你需要的一切，尽在一处',
  homeToolsSubtitle: '四个必备文件工具。零上传、零注册，选一个就能开始。',

  // Tool names & descriptions
  toolVideo: '视频转换',
  toolAudio: '音频转换',
  toolImage: '图片压缩',
  toolZip: 'ZIP 工具',
  toolVideoDesc: '将 MP4、MOV、MKV、WEBM 视频转换为 MP4 或 WEBM。',
  toolAudioDesc: '转码 MP3、WAV、AAC 音频文件。',
  toolImageDesc: '压缩 JPG、PNG、WEBP 图片，体积立减。',
  toolZipDesc: '即时创建和解压 ZIP 压缩包。',

  // Features
  featPrivacyTitle: '隐私优先',
  featPrivacyDesc:
    '所有文件都在浏览器中通过 WebAssembly 处理，不会上传、记录或追踪任何内容。',
  featFastTitle: '极速',
  featFastDesc: '原生 WASM 性能跑在你的设备上 — 无需排队，无需等待服务器往返。',
  featEdgeTitle: '边缘驱动',
  featEdgeDesc: '基于 Cloudflare Workers、D1、KV 和 R2 构建，在全球 300+ 城市边缘节点提供服务。',

  // Workflow
  flowTitle: '工作原理',
  flowSubtitle:
    '文件在本地管道中流转 — 从 React 到 Web Worker，再到 WASM，最后生成 Blob，服务器全程无感。',
  flowSelectTitle: '选择',
  flowSelectDesc: '选择文件 — 拖拽或从设备中浏览。',
  flowProcessTitle: '处理',
  flowProcessDesc: 'WebAssembly 在 Web Worker 中运行，你的设备负责所有计算。',
  flowGenerateTitle: '生成',
  flowGenerateDesc: '结果在本地作为 Blob 生成，不涉及任何网络请求。',
  flowDownloadTitle: '下载',
  flowDownloadDesc: '即刻保存，你的数据从未离开浏览器。',

  // CTA band
  ctaTitle: '文件本地处理，无需注册、无需上传。',
  ctaSubtitle: '选一个工具，几秒钟内开始转换。',

  // File uploader
  upDrop: '松手即可',
  upDrag: '拖拽文件到这里',
  upOr: '或',
  upBrowse: '浏览',
  upFromDevice: '从设备中选择',
  upLimit: '{formats} — 最大 {maxSizeMB} MB',
  upLimitPerFile: '单文件最大 {maxSizeMB} MB',
  upInvalidType: '不支持的文件类型 "{ext}"。支持：{accept}',
  upTooLarge: '"{name}" 文件过大，最大支持 {maxSize}。',

  // File info
  fiSize: '{size}',

  // Progress
  progressDefault: '处理中…',

  // Result card
  resultDone: '完成 — 文件已就绪，可以下载',

  // ZIP compress
  compCreating: '正在创建压缩包…',
  compProgress: '压缩中',
  compSelectedOne: '已选择 {n} 个文件',
  compSelectedMany: '已选择 {n} 个文件',
  compClear: '全部清除',
  compAction: '压缩为 ZIP',
  compFailed: '压缩失败，请换一批文件重试。',

  // ZIP extract
  extExtracting: '正在解压…',
  extProgress: '解压中',
  extExtractedOne: '已解压 {n} 个文件',
  extExtractedMany: '已解压 {n} 个文件',
  extSave: '保存',
  extDownloadAll: '全部下载',
  extNewArchive: '新的压缩包',
  extAction: '解压',
  extFailed: '无法解压该压缩包，文件可能已损坏或受密码保护。',

  // Image
  imgOutputFormat: '输出格式',
  imgSelectFormat: '选择格式',
  imgFormatWebp: 'WebP（推荐）',
  imgQuality: '质量',
  imgAction: '压缩图片',
  imgFailed: '压缩失败，请换一张图片重试。',
  imgProcessing: '正在压缩图片…',
  imgProgress: '压缩中',
  imgResultComplete: '压缩完成',
  imgPreviewAlt: '压缩后预览',
  imgLoadingPreview: '加载预览中…',
  imgOriginal: '原始',
  imgCompressed: '压缩后',
  imgSaved: '节省',
  imgLarger: '增大了 {pct}%',
  imgNewImage: '压缩新图片',

  // Media convert (video/audio)
  mediaLoading: '正在加载转换引擎…',
  mediaConverting: '转换中…',
  mediaEngineNote: '首次使用需下载 WASM 引擎（之后会缓存）',
  mediaBrowserNote: '全程在浏览器本地运行',
  mediaOutputFormat: '输出格式',
  mediaSelectFormat: '选择格式',
  mediaAction: '转换',
  mediaLocalNote: '文件通过 WebAssembly 在本地处理 — 不会上传任何数据。',
  convertFailed: '转换失败，请换一个文件重试。',

  // Page SEO
  seoHomeTitle: 'CloudTools — 你的文件。你的浏览器。你的隐私。',
  seoHomeDesc:
    '无需上传即可转换、压缩和管理文件。基于 WebAssembly 与 Cloudflare Edge 的隐私优先在线工具。',
  seoHomeKw: '在线转换工具, 视频转换, 图片压缩, zip工具, 隐私优先, 浏览器wasm',
  seoVideoTitle: '在线 MP4 转换器 — 免费视频转换工具',
  seoVideoDesc:
    '在浏览器中将 MP4、MOV、MKV、WEBM 视频转换为 MP4 或 WEBM。无需上传，文件永不离开你的设备。',
  seoVideoKw: 'mp4转换器, 视频转换, webm转换, 在线视频转换',
  seoAudioTitle: '在线音频转换器 — MP3 WAV AAC',
  seoAudioDesc:
    '使用 WebAssembly 在浏览器中转换 MP3、WAV、AAC 音频文件。私密、快速、免费。',
  seoAudioKw: '音频转换, mp3转换, wav转换, 在线音频转换',
  seoImageTitle: '在线图片压缩 — JPG PNG WEBP 压缩工具',
  seoImageDesc:
    '在浏览器中压缩 JPG、PNG、WEBP 图片，直观对比压缩前后大小与节省比例。无需上传，完全隐私。',
  seoImageKw: '图片压缩, jpg压缩, png压缩, webp压缩',
  seoZipTitle: '在线 ZIP 工具 — 压缩与解压',
  seoZipDesc: '在浏览器中创建和解压 ZIP 压缩包，文件永不离开你的设备。',
  seoZipKw: 'zip工具, zip压缩, 在线解压, zip解压器',
  seo404Title: '页面未找到 — CloudTools',
  seo404Desc: '您访问的页面不存在。',

  // Tool page headers
  toolPageVideo: '视频转换器',
  toolPageVideoDesc:
    '将 MP4、MOV、MKV、WEBM 视频转换为 MP4 或 WEBM — 由浏览器中的 FFmpeg.wasm 驱动。',
  toolPageAudio: '音频转换器',
  toolPageAudioDesc:
    '使用 FFmpeg.wasm 在本地转码 MP3、WAV、AAC 音频 — 音频永不离开你的浏览器。',
  toolPageImage: '图片压缩器',
  toolPageImageDesc: '本地压缩 JPG、PNG、WEBP 图片，画质可调 — 图片永不离开你的浏览器。',
  toolPageZip: 'ZIP 工具',
  toolPageZipDesc: '在浏览器内完成文件打包与解压。',

  // ZIP mode switch
  modeCompress: '压缩',
  modeExtract: '解压',

  // 404
  nfBody: '你访问的页面不存在。',

  // Footer
  footTagline:
    '在浏览器内完成文件转换、压缩与管理。由 WebAssembly 驱动 — 文件永不离开你的设备。',
  footTools: '工具',
  footCompany: '公司',
  footPrivacy: '隐私',
  footApi: 'API',
  footPricing: '定价',
  footRights: '© {year} CloudTools。所有处理均在浏览器本地完成。',
  footApiVersion: 'API v{version}',
}